/**
 * Management dashboard navigation structure.
 *
 * The dashboard is reorganized into four logical groups so the site owner can
 * move between editorial, business and intelligence work without a long
 * horizontal tab rail. All sections remain front-end demos only.
 */

export type SectionId =
  | 'command-center'
  | 'editorial'
  | 'tournaments'
  | 'bulletins'
  | 'multilingual'
  | 'sponsors'
  | 'revenue'
  | 'fundraising'
  | 'audience'
  | 'analytics'
  | 'language'
  | 'ai-activity';

export type NavItem = {
  id: SectionId;
  label: string;
  icon: string;
  description: string;
};

export type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'command-center',
        label: 'Command Center',
        icon: 'ri-dashboard-3-line',
        description: 'Your operations homepage — everything waiting on you today, in one place.',
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      {
        id: 'editorial',
        label: 'Editorial / Stories',
        icon: 'ri-newspaper-line',
        description: 'Incoming stories, AI-prepared drafts and the full review pipeline.',
      },
      {
        id: 'tournaments',
        label: 'Tournaments',
        icon: 'ri-trophy-line',
        description: 'Submit, review and publish golf tournament listings.',
      },
      {
        id: 'bulletins',
        label: 'Community Bulletins',
        icon: 'ri-pushpin-2-line',
        description: 'Moderate community notices before they reach the public board.',
      },
      {
        id: 'multilingual',
        label: 'Multilingual Publishing',
        icon: 'ri-translate-2',
        description: 'Review and manage each language version of a story independently.',
      },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    items: [
      {
        id: 'sponsors',
        label: 'Sponsors & Leads',
        icon: 'ri-contacts-book-2-line',
        description: 'The sponsor CRM — leads, pipeline, notes and follow-ups in one place.',
      },
      {
        id: 'revenue',
        label: 'Sponsorships & Revenue',
        icon: 'ri-line-chart-line',
        description: 'Sponsorship pipeline, packages, promotions, registrations and renewals.',
      },
      {
        id: 'fundraising',
        label: 'Charity Fundraising',
        icon: 'ri-hand-heart-line',
        description: 'Charity tournament fundraising concepts and regional progress.',
      },
      {
        id: 'audience',
        label: 'Audience & Subscribers',
        icon: 'ri-group-line',
        description: 'Newsletter subscribers and audience growth.',
      },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    items: [
      {
        id: 'analytics',
        label: 'Regional Analytics',
        icon: 'ri-bar-chart-2-line',
        description: 'Traffic, audience and engagement by state and region.',
      },
      {
        id: 'language',
        label: 'Language Analytics',
        icon: 'ri-global-line',
        description: 'Preferred language, engagement and translation coverage — sample data.',
      },
      {
        id: 'ai-activity',
        label: 'AI Activity Preview',
        icon: 'ri-sparkling-line',
        description: 'A conceptual preview of automated editorial intelligence.',
      },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

export function sectionById(id: SectionId): NavItem {
  return NAV_ITEMS.find((item) => item.id === id) ?? NAV_ITEMS[0];
}